import { useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import { SeoReport, METRIC_COLUMNS, MetricKey, PageGroup, isMeasured } from './seoTypes';
import { groupSum, groupSumValue, groupSeries, pageMetric, pagePrevMetric, groupPrevSum, GroupMonthSum } from './seoCompute';
import { ValueDelta, Sparkline, fmtInt, fmtMetric, pct, prettyMonth } from './seoFormat';

interface Props {
    report: SeoReport;
    sel: number;
}

type SortCol = MetricKey | null;

export const SeoSummary = ({ report, sel }: Props) => {
    const [sortCol, setSortCol] = useState<SortCol>(null);
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');
    const prev = sel - 1;

    const rows = report.groupOrder.map((gn: PageGroup) => ({
        gn,
        cur: groupSum(report.groups[gn], sel),
        prev: groupPrevSum(report, report.groups[gn], sel),
        series: groupSeries(report.groups[gn], report.months.length),
    }));

    if (sortCol) {
        rows.sort((a, b) => {
            const av = groupSumValue(a.cur, sortCol);
            const bv = groupSumValue(b.cur, sortCol);
            return dir === 'asc' ? av - bv : bv - av;
        });
    }

    const onSort = (col: MetricKey) => {
        if (sortCol === col) setDir(dir === 'asc' ? 'desc' : 'asc');
        else {
            setSortCol(col);
            setDir('desc');
        }
    };

    // A metric that was not being recorded yet renders "—", never 0. A zero here
    // reads as "nobody converted", which is how a broken counter got mistaken for
    // a collapsing funnel. The delta is suppressed too: a change against an
    // unmeasured baseline is not a change.
    const unmeasuredCell = (key: MetricKey, bold?: boolean) => (
        <TableCell key={key} align="right">
            <Typography
                component="span"
                variant="body2"
                color="text.disabled"
                sx={{ fontWeight: bold ? 700 : 400 }}
                title="Not recorded in this month — see the note below the table."
            >
                —
            </Typography>
        </TableCell>
    );

    const cell = (cur: GroupMonthSum, prevSum: GroupMonthSum | null, key: MetricKey) => {
        if (!isMeasured(report, key, sel, 'group')) return unmeasuredCell(key);
        const v = groupSumValue(cur, key);
        const pv = prevSum ? groupSumValue(prevSum, key) : null;
        const comparable = prevSum != null && isMeasured(report, key, prev, 'group');
        return (
            <TableCell key={key} align="right">
                <ValueDelta text={fmtMetric(key, v)} p={comparable ? pct(v, pv) : null} lowerBetter={key === 'position'} />
            </TableCell>
        );
    };

    // Site-wide "All pages" total (includes untracked pages), pinned at the bottom.
    const totals = report.totals;
    const totalsCell = (key: MetricKey) => {
        if (!isMeasured(report, key, sel, 'total')) return unmeasuredCell(key, true);
        const v = pageMetric(totals, key, sel);
        const pv = pagePrevMetric(report, totals, key, sel);
        const comparable = pv != null && isMeasured(report, key, prev, 'total');
        return (
            <TableCell key={key} align="right">
                <ValueDelta text={fmtMetric(key, v)} p={comparable ? pct(v, pv) : null} lowerBetter={key === 'position'} bold />
            </TableCell>
        );
    };

    const isMtd = report.months[sel] === report.mtdMonth;

    // Calls have no page dimension, so they sit under the table rather than in
    // it. Compare against the clipped prior month when the current one is
    // partial, exactly as the table columns do.
    const calls = report.calls;
    const coverage = report.meta.coverage;
    const callsPrev = !calls
        ? null
        : isMtd && calls.pmtd
            ? calls.pmtd.calls
            : prev >= 0
                ? calls.calls[prev] ?? null
                : null;

    return (
        <Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Group</TableCell>
                            {METRIC_COLUMNS.map((c) => (
                                <TableCell key={c.key} align="right" sortDirection={sortCol === c.key ? dir : false}>
                                    <TableSortLabel
                                        active={sortCol === c.key}
                                        direction={sortCol === c.key ? dir : 'desc'}
                                        onClick={() => onSort(c.key)}
                                    >
                                        {c.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell>Trend</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.gn}>
                                <TableCell sx={{ fontWeight: 600 }}>{r.gn}</TableCell>
                                {METRIC_COLUMNS.map((c) => cell(r.cur, r.prev, c.key))}
                                <TableCell>
                                    <Sparkline data={r.series} />
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', bgcolor: '#fafafa' } }}>
                            <TableCell sx={{ fontWeight: 700 }} title="Every page on the site, including pages not in the tracked groups (organic search only).">
                                All pages
                            </TableCell>
                            {METRIC_COLUMNS.map((c) => totalsCell(c.key))}
                            <TableCell>
                                <Sparkline data={totals.impr} />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            {calls && (
                <Box
                    sx={{
                        mt: 1.5, px: 2, py: 1.25, border: '1px solid', borderColor: 'divider',
                        borderRadius: 1, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Inbound calls received
                    </Typography>
                    <ValueDelta
                        text={fmtInt(calls.calls[sel] ?? 0)}
                        p={callsPrev != null ? pct(calls.calls[sel] ?? 0, callsPrev) : null}
                    />
                    <Typography variant="caption" color="text.secondary">
                        ({fmtInt(calls.answered[sel] ?? 0)} answered)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        Calls that reached us — not the same as phone clicks.
                    </Typography>
                </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Showing <b>{prettyMonth(report.months[sel], report.mtdMonth)}</b>
                {isMtd && report.meta.priorMtdLabel ? (
                    <> with change vs the <b>same period last month</b> ({report.meta.priorMtdLabel}) — an apples-to-apples month-to-date comparison.</>
                ) : prev >= 0 ? (
                    <> with month-over-month change vs {prettyMonth(report.months[prev], report.mtdMonth)}.</>
                ) : '.'}
                {' '}<b>All pages</b> is the site-wide total including pages outside the tracked groups. Conversion metrics count all non-paid traffic (every channel except Paid Search); impressions, clicks &amp; position are organic search (Search Console).
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                <b>Where these numbers come from.</b>{' '}
                <b>Form fills</b> and <b>phone clicks</b> are counted from our own database
                (<code>form_submissions</code>, <code>phone_click_events</code>), not from GA4 events — GA4 discarded
                any conversion that happened before Google Tag Manager finished loading, which made both series
                unreliable for their whole history.{' '}
                <b>Form starts</b> still comes from GA4 enhanced measurement, so treat it as directional only.{' '}
                A <b>—</b> means the metric was not being recorded that month; it never means zero.
                {coverage?.fillsByGroup && (
                    <> Form fills can only be credited to a page group from <b>{prettyMonth(coverage.fillsByGroup, null)}</b>,
                    when the landing page began being captured; before that they are counted in <b>All pages</b> only,
                    and no backfill can recover the attribution.</>
                )}
                {' '}<b>Inbound calls</b> counts calls that reached us, including people who found the number on Google
                Business Profile or had it saved, so it is a different measure from phone clicks and the two will not
                match. Its organic/paid split is decided largely by which number was dialled, and a tracking bug fixed
                on 2026-08-26 showed the ads number to a large share of social-share visitors, so organic is
                understated in earlier months.
            </Typography>
        </Box>
    );
};
